import React, { FC, useEffect, useState, useCallback, RefObject, useRef } from 'react';
import { Box, Typography, Grid, Button, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store';
import useStyles from './styles';

import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { getConfigFilesRequestBody } from '../../../services/types';
import {
  State as FirmwareStateType,
} from '../../../entities/remote-flash-v1/models';
import { setModalOpen, setModalClosed } from '../../../store/modal/actions';
import { ShortMessagePostRequest } from '../../../entities/short-message-request-configuration-v1/short-message-post-request';
import { getRemoteFlashStatus, shortMessageRequestConfigAction } from '../../../store/commands/general-commands/actions';
import Table from '../table';
import { softwareHeadCells, softwareSortableAccessors } from '../table/head-cells';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import FileUploader from '../../file-loader';
import { pollingWaitForNewCommandByDeviceKey } from '../../../store/commands/actions';
import { useMountEffect } from '../../../globals/hooks/use-mount-effect';
import ProgressBar from '../../progress-bar';
import { getSoftwareStatusByDeviceKey, setIsDownloadButtonDisabled } from '../../../store/configure/actions';
import {
  queueExistingFile,
  updateSoftwareMetadataAction,
  uploadBatchSoftwareConfigFile,
} from '../../../store/configure/remote-flash/actions';
import { RemoteFlashActionNames } from '../../../store/configure/remote-flash/types';

const Software: FC = () => {
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const softwareStatus = useSelector((state: AppState) => state.firmware.firmwareStatus);
  const { isDownloadButtonDisabled } = useSelector((state: AppState) => state.configSoftware);
  const fileExistsInDatabase = useSelector(
    (state: AppState) => state.firmware.softwareFileExistsInDb,
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const [pushSoftwareFileName, setPushSoftwareFileName] = useState<string>('');
  const [softwareConfigFilesData, setSoftwareConfigFilesData] = useState<any>({});
  const [fileToPush, setFileToPush] = useState<any>();
  const [tableData, setTableData] = useState<any[]>([]);
  const deviceKey = selectedEntity?.deviceKey;
  const deviceId = selectedEntity?.deviceId;
  const clientAuth: any = client?.authenticate();
  const {
    metadata: { catloginid },
  } = clientAuth;
  const [percentUpload, setPercentUpload] = useState<number>(0);
  const [partNumber, setPartNumber] = useState<string | undefined>(undefined);
  const readyToFlash = softwareStatus?.state === FirmwareStateType.ReadyToFlash ||
    (selectedEntity?.deviceType === 'PL444' && (softwareStatus?.status === 'N/A' || !softwareStatus?.status)) ||
    (selectedEntity?.deviceType === 'PL143' && (softwareStatus?.status === 'N/A' || !softwareStatus?.status));

  const tableContainer: RefObject<HTMLDivElement> = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);

  const getConfigurationFiles = async () => {
    if (!tableData) setTableData([]);
    if (deviceKey) {
      const configFilesLibrary = new ConfigurationFilesLibrary(
        client,
        envVariables.configurationFilesLibraryV1BaseUrl,
      );
      const input: getConfigFilesRequestBody = { fileTypeId: '10030', isRetrievable: true, limit: 500 };
      const response = await configFilesLibrary.getConfigFiles(input);
      const configFilesLibraries = response.data.configs;
      if (configFilesLibraries) {
        setTableData(configFilesLibraries);
      } else {
        setTableData([]);
      }
    }
  };

  useEffect(() => {
    if (softwareStatus?.state === FirmwareStateType.ReadyToFlash) {
      dispatch(setIsDownloadButtonDisabled(false));
    } else {
      dispatch(setIsDownloadButtonDisabled(true));
    }
  }, [softwareStatus]);

  const getSoftwareStatus = useCallback(async () => {
    if (deviceKey) {
      dispatch(getSoftwareStatusByDeviceKey({ deviceKey }));
    }
  }, [deviceKey, dispatch]);

  useEffect(() => {
    getSoftwareStatus();
  }, [selectedEntity]);

  const fileHandler = async (file: any) => {
    setPushSoftwareFileName(file.name);
    setFileToPush(file);
    if (partNumber) dispatch(setIsDownloadButtonDisabled(false));
  };

  useMountEffect(() => {
    getConfigurationFiles();
  });

  const handlePartNumber = (event: any) => {
    setPartNumber(event.target.value);
    if (fileToPush) {
      dispatch(setIsDownloadButtonDisabled(false));
    }
  };

  const getCurrentSoftware = async () => {
    if (deviceKey) {
      const shortMessageRequsetInputs: ShortMessagePostRequest = {
        configs: [{ deviceKey, fileTypeId: '10088' },
        { deviceKey, fileTypeId: '10213' },
        { deviceKey, fileTypeId: '10696' },
        { deviceKey, fileTypeId: '10554' }
        ],
        userId: catloginid,
      };
      const classificationType = { classification: 'PLMessage' };
      await dispatch(shortMessageRequestConfigAction(shortMessageRequsetInputs, deviceKey, classificationType));
    }
  };

  const handleChnage = async () => {
    if (deviceKey) {
      await dispatch(getRemoteFlashStatus({ deviceKey }));
    }
  };

  const forceUnlockDeviceSoftware = () => {
    if (deviceKey && softwareStatus) {
      const deviceData = {
        userId: catloginid,
        forceUnlock: true,
        state: `Ready to Flash`,
        status: 'Success',
      };
      dispatch(updateSoftwareMetadataAction(deviceData));
      getSoftwareStatus();
    }
    dispatch(setIsDownloadButtonDisabled(false));
  };

  const addToQueue = async () => {
    if (partNumber !== '') {
      const pushSoftwareConfigData = {
        file: fileToPush,
        fileTypeId: '10030',
        partNumber,
        setPercentUpload,
        id: softwareStatus?.globalFileSha ? softwareStatus?.globalFileSha : '',
        applicationId: softwareStatus?.lastUpdatedBySystem,
        overwrite: false,
        isRetrievable: false,
        overwriteCallback: ({ isNewRetrievable, uiStatusCode, hashId, transactionNewId, userId }: any) => dispatch(setModalOpen({
          open: true,
          message: `A file with identical contents names ${fileToPush.name} already exists in the database and cannot be overwritten. Would you like to proceed to queue the existing software from database?`,
          accept: async () => {
            await dispatch(uploadBatchSoftwareConfigFile({
              ...pushSoftwareConfigData,
              overwrite: true,
              isRetrievable: isNewRetrievable,
              uiStatusCode,
              hashId,
              transactionNewId,
              userId,
            }));
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            });
            getConfigurationFiles();
          },
          cancel: () => {
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            })
          }
        }))
      };
      setSoftwareConfigFilesData(pushSoftwareConfigData);
      await dispatch(uploadBatchSoftwareConfigFile(pushSoftwareConfigData));
      setFileToPush('');
      setPushSoftwareFileName('');
      setPartNumber(undefined);
      dispatch(setIsDownloadButtonDisabled(true));
      getSoftwareStatus();
      dispatch(pollingWaitForNewCommandByDeviceKey());
      getConfigurationFiles();
    } else {
      dispatch(
        enqueueSnackbarAction({
          message: 'Part number cannot be empty',
          options: { variant: 'error' },
        }),
      );
    }
  };

  const handleSoftwareFileOverwrite = async (dispatch: any) => {
    const replaceFileData = { ...softwareConfigFilesData, overwrite: true, isRetrievable: true };
    if (!tableData) {
      return;
    }
    const existingRetrievableFile = tableData?.find(item => item.id === (fileExistsInDatabase as any).hash);
    if (existingRetrievableFile) {
      dispatch(
        setModalOpen({
          open: true,
          message: `A file that contains identical contents of the uploaded file already exists and is named ${existingRetrievableFile.fileName}. The existing file will be queued. `,
          accept: () => {
            dispatch(queueExistingFile(fileExistsInDatabase as any))
            setModalClosed();
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            });
          },
          cancel: () => {
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            });
          },
        }),
      );
    } else {
      await dispatch(uploadBatchSoftwareConfigFile(replaceFileData));
    }
  };

  useEffect(() => {
    if (fileExistsInDatabase) {
      handleSoftwareFileOverwrite(dispatch);
    }
  }, [fileExistsInDatabase, dispatch]);

  const TableData = {
    id: "firmware-config-file-library-table",
    title: 'Software',
    storageString: 'configure-software',
    headerColumns: softwareHeadCells,
    sortableAccessors: softwareSortableAccessors,
    onRowSelection: setSelectedRows,
    data: tableData,
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    tableContainer,
  };

  return (
      <Grid container direction="row" id="software-grid-container">
        <Grid item xs={2}>
          <Box pb={2}>
            <Typography variant="h6">Status</Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid container direction="row">
            <Grid item xs={4}>
              <Box px={1} pt={2} pb={1} display="flex" id="current-software-id-label">
                <Typography variant="h6">Current Software</Typography>
                <Box pl={2} id="current-software-id">
                  <Typography>
                    {softwareStatus?.reportedFirmware ? softwareStatus?.reportedFirmware : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box px={1} pt={2} pb={1} display="flex" id="pending-software-state-id-label">
                <Typography variant="h6">Pending Software State</Typography>
                <Box pl={2} id="pending-software-state-id">
                  <Typography>{softwareStatus?.state ? softwareStatus?.state : 'N/A'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box px={1} pt={2} pb={1} display="flex" id="pending-software-status-id-label">
                <Typography variant="h6">Pending Software Status</Typography>
                <Box pl={2} id="pending-software-status-id">
                  <Typography>{softwareStatus?.status ? softwareStatus?.status : 'N/A'}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container direction="row">
            <Grid item xs={4}>
              <Box px={1} pb={1} display="flex">
                <Button
                  id="request-current-software-status"
                  variant="outlined"
                  onClick={() => getCurrentSoftware()}
                >
                  Request Current Software
                </Button>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box px={1} pb={1} display="flex" >
                <Button
                  id="request-current-software-status"
                  variant="outlined"
                  onClick={handleChnage}
                >
                  Request Flash Status
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">Update Software</Typography>
          <Box
            p={1}
            pb={2}
            flexGrow={0}
            className={classes.noWrap}
            justifyContent="space-between"
            alignItems="center"
          >
            {!percentUpload ? (
              <>
                <Box
                  p={1}
                  pb={2}
                  className={classes.noWrap}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box p={1} pr={0}>
                    <Typography variant="h6">Upload Software File:</Typography>
                  </Box>
                  <Box p={1} pr={0}>
                    <FileUploader enabled={readyToFlash} handleFile={fileHandler} />
                  </Box>
                  <Box p={1} pr={0}>
                    <Typography variant="h6">File Name:</Typography>
                  </Box>
                  <Box p={1} flexGrow={0} overflow="hidden">
                    {pushSoftwareFileName && (
                      <Typography className={classes.overFlowEllipsis}>
                        {pushSoftwareFileName}
                      </Typography>
                    )}
                  </Box>
                  <Box p={1} pr={0}>
                    <Typography variant="h6">Part Number:</Typography>
                  </Box>
                  <Box p={1} mt="-16px">
                    <TextField
                      variant="standard"
                      className={classes.partNumber}
                      margin="none"
                      id="part-number-input"
                      label="Part#"
                      onChange={handlePartNumber}
                      required
                    />
                  </Box>
                  <Box display="flex" flexGrow={1} justifyContent="flex-end" pr={2}>
                    <Button
                      className={classes.submitButton}
                      onClick={addToQueue}
                      disabled={!(fileToPush && !isDownloadButtonDisabled && partNumber)}
                      variant="outlined"
                      size="small"
                      color="primary"
                      endIcon
                    >
                      Add to Queue
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Box p={1} display="flex" flexGrow={1} width="100%" alignItems="center">
                  <ProgressBar percentUpload={percentUpload} />
                </Box>
              </>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid item>
              <Box px={1} pb={1}>
                <Button
                  id="unlock-device-software"
                  variant="outlined"
                  onClick={() => forceUnlockDeviceSoftware()}
                >
                  Unlock
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {tableData && (
            <div ref={tableContainer} className={classes.tableContainer}>
              <Table {...TableData} />
            </div>
          )}
        </Grid>
      </Grid>
  );
};

export default Software;
